import React from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Typography,
  useTheme,
} from "@mui/material";

/**
 * Truncates `text` to `maxLength` characters, appending "…" when trimmed.
 * Used as a fallback when a post has no explicit excerpt.
 */
const truncate = (text, maxLength = 160) => {
  if (!text || text.length <= maxLength) return text;
  return text.slice(0, maxLength).trimEnd() + "…";
};

/**
 * Formats an ISO date string into a human-readable form, e.g. "March 9, 2026".
 * Falls back to the raw string if parsing fails.
 */
const formatDate = (isoString) => {
  if (!isoString) return null;
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return isoString;
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(date);
};

/**
 * A single post summary card. The entire card surface is a link to /blog/:slug.
 */
const PostCard = ({ post }) => {
  const theme = useTheme();

  const excerpt =
    post.excerpt
      ? post.excerpt
      : truncate(post.body);

  const publishedDate = formatDate(post.published_at);

  return (
    // Wrapping with Link makes the whole card keyboard- and click-navigable
    // without nesting interactive elements. `display: block` avoids an inline
    // anchor that would shrink-wrap to content and break the card layout.
    <Link
      to={`/blog/${post.slug}`}
      style={{ display: "block", textDecoration: "none" }}
    >
      <Card
        variant="outlined"
        sx={{
          mb: 2,
          backgroundColor: "background.paper",
          borderColor: "divider",
          cursor: "pointer",
          transition: "border-color 0.15s ease, box-shadow 0.15s ease",
          "&:hover": {
            borderColor: theme.palette.primary.main,
            boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
          },
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {/* Title */}
          <Typography
            variant="h6"
            component="h2"
            sx={{
              color: theme.palette.primary.main,
              fontFamily: "'Fira Code', monospace",
              fontWeight: 600,
              mb: 0.5,
              lineHeight: 1.3,
            }}
          >
            {post.title}
          </Typography>

          {/* Published date */}
          {publishedDate && (
            <Typography
              variant="caption"
              component="time"
              dateTime={post.published_at}
              sx={{
                display: "block",
                color: theme.palette.comment.main,
                fontFamily: "'Fira Code', monospace",
                mb: 1.5,
              }}
            >
              {/* Rendered as an inline comment to match the VS Code aesthetic */}
              {`// ${publishedDate}`}
            </Typography>
          )}

          {/* Excerpt */}
          {excerpt && (
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.primary,
                mb: 2,
                lineHeight: 1.65,
              }}
            >
              {excerpt}
            </Typography>
          )}

          {/* Tags */}
          {Array.isArray(post.tags) && post.tags.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {post.tags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  sx={{
                    // keyword.main is the purple VS Code token color — a natural
                    // fit for tags, which act as semantic labels just like keywords.
                    color: theme.palette.keyword.main,
                    borderColor: theme.palette.keyword.main,
                    backgroundColor: "transparent",
                    fontFamily: "'Fira Code', monospace",
                    fontSize: "0.7rem",
                  }}
                  variant="outlined"
                />
              ))}
            </Box>
          )}
        </CardContent>
      </Card>
    </Link>
  );
};

export default PostCard;
